import React, { useEffect, useState, useCallback } from "react";
import { listAdvisors, approveAdvisor, rejectAdvisor } from "../../api/adminApi";

const Card = ({ children, className = "" }) => (
  <div
    className={`p-5 rounded-2xl bg-white/60 dark:bg-[#1a2332]/60 backdrop-blur-xl border border-white/40 dark:border-white/10 shadow-glass dark:shadow-glass-dark ${className}`}
  >
    {children}
  </div>
);

const STATUS_STYLES = {
  draft:     "bg-amber-500/15 text-amber-500",
  in_review: "bg-sky-500/15 text-sky-500",
  approved:  "bg-emerald-500/15 text-emerald-500",
  suspended: "bg-red-500/15 text-red-500",
};

const FILTERS = [
  { value: "", label: "All" },
  { value: "in_review", label: "Pending review" },
  { value: "approved", label: "Approved" },
  { value: "draft", label: "Draft" },
  { value: "suspended", label: "Suspended" },
];

const AdminAdvisors = () => {
  const [advisors, setAdvisors] = useState([]);
  const [filter, setFilter] = useState("in_review");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setAdvisors(await listAdvisors(filter));
    } catch {
      setError("Couldn't load advisors.");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  const decide = async (advisor, action) => {
    setError("");
    setBusyId(advisor.id);
    try {
      const updated = action === "approve"
        ? await approveAdvisor(advisor.id)
        : await rejectAdvisor(advisor.id);
      setAdvisors((list) =>
        list.map((a) => (a.id === advisor.id ? { ...a, ...updated } : a)),
      );
    } catch {
      setError("Action failed. Please try again.");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold">Advisors</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Review marketplace applications. Approving lists the advisor publicly.
          </p>
        </div>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="px-3 py-2 rounded-lg border border-gray-300 dark:border-white/15 bg-white dark:bg-gray-950 text-sm"
        >
          {FILTERS.map((f) => (
            <option key={f.value || "all"} value={f.value}>{f.label}</option>
          ))}
        </select>
      </div>

      {error && (
        <div className="px-4 py-3 rounded-xl bg-red-500/10 text-red-500 text-sm">{error}</div>
      )}

      {loading ? (
        <Card>Loading…</Card>
      ) : advisors.length === 0 ? (
        <Card>
          <p className="text-sm text-gray-500">No advisors match this filter.</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {advisors.map((a) => (
            <Card key={a.id}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-bold text-lg">{a.display_name || a.slug}</h3>
                    <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${STATUS_STYLES[a.review_status] || "bg-gray-500/15 text-gray-500"}`}>
                      {a.review_status}
                    </span>
                    {a.jurisdiction && (
                      <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-gray-500/10 text-gray-500">
                        {a.jurisdiction}
                      </span>
                    )}
                  </div>
                  {a.headline && (
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{a.headline}</p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    KYC: {a.kyc_status || "—"} · /{a.slug}
                  </p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button
                    type="button"
                    disabled={busyId === a.id || a.review_status === "approved"}
                    onClick={() => decide(a, "approve")}
                    className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-500 disabled:opacity-40"
                  >
                    Approve
                  </button>
                  <button
                    type="button"
                    disabled={busyId === a.id || a.review_status === "suspended"}
                    onClick={() => decide(a, "reject")}
                    className="px-3 py-1.5 rounded-lg text-red-500 border border-red-500/30 hover:bg-red-500/10 text-sm font-semibold disabled:opacity-40"
                  >
                    Reject
                  </button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminAdvisors;
